import ServiceStore from "stores/ServiceStore";
import { RouteType } from "types/RouteType";

const masterPieceListRoute: RouteType = {
  name: "Master Piece List",
  path: "/master-piece-list",
  layout: "/app",
  group: "Production",
  requiresAuth: true,
  requiresPermission: false,
  projectRoute: false,
  hidden: false,
  viewportCard: {
    header: {
      title: "Master Piece List",
      component: () => import("./MasterPieceList.Header"),
      enableRefreshButton: false,
      enableBackButton: true,
    },
    body: {
      component: () => import("./MasterPieceList.Body"),
      grayBackground: true,
    },
  },
  channels: ["panels", "production-schedule", "panel-schedule-chips", "mold-instances", "projects"],
  beforeEnter: async ({ rootStore }) => {
    try {
      const promises: Promise<unknown>[] = [];

      // The MPL grid needs EVERY panel and EVERY chip, because each panel's
      // Setup / Cast / RTS date columns are derived from its phase chips
      // (moldProduction, setup, casting day 1..N, finishing day 1..N, shipping
      // — ~5+ per panel).
      //
      // This used to be a flat `{ $limit: 15000 }`, which is the backend's
      // `paginate.max` — a HARD server ceiling, not a default that can be
      // raised. On 2026-08-17 production held 18,364 panel_schedule_chips, so
      // ~3,400 were being dropped on every load and the date columns were
      // quietly incomplete. That is the same "blank date columns" symptom the
      // original 1000-row cap produced, recurring at a larger scale — and this
      // time nobody reported it, which suggests the columns are not trusted.
      //
      // So: page through instead of asking for one oversized slice.
      const PAGE_SIZE = 5000;

      /**
       * Fetch every row of a service by paging. Pages are ordered by id so the
       * window is stable between requests — without an explicit $sort, MSSQL
       * may return rows in a different order per page, which would silently
       * duplicate some rows and skip others.
       *
       * ServiceStore.find merges each page into the store (addOrUpdateEvent
       * per row) rather than replacing, so the pages accumulate.
       */
      const findAll = async (store: ServiceStore, label: string): Promise<void> => {
        let skip = 0;
        for (;;) {
          let res = (await store.find({
            query: { $limit: PAGE_SIZE, $skip: skip, $sort: { id: 1 } },
          })) as { data?: unknown[]; total?: number } | unknown[] | null;

          // ServiceStore.find swallows every error and resolves null, so a
          // service that rejects $sort would leave us loading NOTHING — worse
          // than the truncation this replaces. Retry the page unsorted before
          // giving up; paging is then less stable, but partial beats empty.
          if (!res && skip === 0) {
            console.warn(`[MasterPieceList] ${label}: sorted fetch failed, retrying without $sort`);
            res = (await store.find({ query: { $limit: PAGE_SIZE, $skip: skip } })) as typeof res;
          }

          if (!res) {
            console.warn(`[MasterPieceList] ${label}: fetch failed at skip=${skip}; data may be incomplete`);
            return;
          }

          const data = Array.isArray(res) ? res : (res.data ?? []);
          skip += data.length;

          if (data.length < PAGE_SIZE) return; // short page => last page
          if (skip > 200000) {
            console.warn(`[MasterPieceList] ${label}: stopped paging at ${skip} rows (runaway guard)`);
            return;
          }
        }
      };

      const panelsStore = rootStore.panelsStore as ServiceStore;
      if (panelsStore) promises.push(findAll(panelsStore, "panels"));

      const productionScheduleStore =
        rootStore.productionScheduleStore as ServiceStore;
      if (productionScheduleStore) promises.push(findAll(productionScheduleStore, "production-schedule"));

      const panelScheduleChipsStore =
        (rootStore as unknown as { panelScheduleChipsStore?: ServiceStore })
          .panelScheduleChipsStore;
      if (panelScheduleChipsStore) promises.push(findAll(panelScheduleChipsStore, "panel-schedule-chips"));

      const moldInstancesStore =
        (rootStore as unknown as { moldInstancesStore?: ServiceStore })
          .moldInstancesStore;
      if (moldInstancesStore) promises.push(findAll(moldInstancesStore, "mold-instances"));

      const projectsStore =
        (rootStore as unknown as { projectsStore?: ServiceStore }).projectsStore;
      if (projectsStore) promises.push(findAll(projectsStore, "projects"));

      await Promise.all(promises);
      return true;
    } catch (error) {
      console.error("Failed to load master piece list data:", error);
      return true;
    }
  },
};

export default masterPieceListRoute;
